<template>
  <div :class="['sheet-preview-card', { compact }]">
    <div class="sheet-preview-heading">
      <div>
        <span class="etq-panel-kicker">Conferência</span>
        <h6>Folha ou rolo completo</h6>
      </div>
      <span class="sheet-count">{{ total }} por página</span>
    </div>

    <div class="sheet-stage">
      <div class="sheet-page" :style="pageStyle">
        <div
          class="sheet-printable-area"
          :style="printableAreaStyle"
          aria-hidden="true"
        ></div>
        <div
          v-for="position in positions"
          :key="position.index"
          class="sheet-label"
          :style="position.style"
        >
          <span
            v-for="item in miniatureElements"
            :key="`${position.index}-${item.id}`"
            :class="`mini-${item.tipo}`"
            :style="miniatureStyle(item)"
          ></span>
        </div>
      </div>
    </div>

    <div class="sheet-measures">
      <span
        ><strong>{{ pageWidth }} × {{ pageHeight }}</strong> mm página</span
      >
      <i></i>
      <span
        ><strong>{{ labelWidth }} × {{ labelHeight }}</strong> mm etiqueta</span
      >
    </div>

    <div v-if="warnings.length" class="sheet-warning">
      <i class="fas fa-exclamation-triangle"></i>
      <div>
        <strong>Confira antes de imprimir</strong>
        <span v-for="warning in warnings" :key="warning">{{ warning }}</span>
      </div>
    </div>
    <div v-else class="sheet-ok">
      <i class="fas fa-check-circle"></i>
      <span>Medidas compatíveis e elementos dentro da área.</span>
    </div>
  </div>
</template>

<script>
export default {
  name: "EtiquetaSheetPreview",
  props: {
    definition: { type: Object, required: true },
    warnings: { type: Array, default: () => [] },
    compact: { type: Boolean, default: false },
  },
  computed: {
    page() {
      return this.definition.pagina || {};
    },
    label() {
      return this.definition.etiqueta || {};
    },
    pageWidth() {
      return Number(this.page.largura_mm || 50);
    },
    pageHeight() {
      return Number(this.page.altura_mm || 30);
    },
    labelWidth() {
      return Number(this.label.largura_mm || 50);
    },
    labelHeight() {
      return Number(this.label.altura_mm || 30);
    },
    columns() {
      return Math.max(1, Number(this.page.colunas || 1));
    },
    rows() {
      return Math.max(1, Number(this.page.linhas || 1));
    },
    total() {
      return this.columns * this.rows;
    },
    scale() {
      return this.compact
        ? Math.min(240 / this.pageWidth, 125 / this.pageHeight)
        : Math.min(260 / this.pageWidth, 280 / this.pageHeight);
    },
    pageStyle() {
      return {
        width: `${this.pageWidth * this.scale}px`,
        height: `${this.pageHeight * this.scale}px`,
      };
    },
    printableAreaStyle() {
      const left = Math.max(0, Number(this.page.margem_esquerda_mm || 0));
      const right = Math.max(0, Number(this.page.margem_direita_mm || 0));
      const top = Math.max(0, Number(this.page.margem_superior_mm || 0));
      const bottom = Math.max(0, Number(this.page.margem_inferior_mm || 0));
      const width = Math.max(0, this.pageWidth - left - right);
      const height = Math.max(0, this.pageHeight - top - bottom);

      return {
        left: `${Math.min(left, this.pageWidth) * this.scale}px`,
        top: `${Math.min(top, this.pageHeight) * this.scale}px`,
        width: `${width * this.scale}px`,
        height: `${height * this.scale}px`,
        zIndex: 1,
      };
    },
    positions() {
      const positions = [];
      const horizontal =
        this.labelWidth + Number(this.page.espacamento_horizontal_mm || 0);
      const vertical =
        this.labelHeight + Number(this.page.espacamento_vertical_mm || 0);
      const left = Number(this.page.margem_esquerda_mm || 0);
      const top = Number(this.page.margem_superior_mm || 0);
      let index = 0;
      for (let row = 0; row < this.rows && index < 60; row += 1) {
        for (let column = 0; column < this.columns && index < 60; column += 1) {
          positions.push({
            index,
            style: {
              left: `${(left + column * horizontal) * this.scale}px`,
              top: `${(top + row * vertical) * this.scale}px`,
              width: `${this.labelWidth * this.scale}px`,
              height: `${this.labelHeight * this.scale}px`,
              background: this.label.fundo || "#FFFFFF",
              borderColor: this.label.cor_borda || "#3f4254",
              borderWidth: `${Math.max(
                0.5,
                Number(this.label.borda_mm || 0) * this.scale
              )}px`,
              borderRadius: `${
                Number(this.label.cantos_arredondados_mm || 0) * this.scale
              }px`,
              zIndex: 2,
            },
          });
          index += 1;
        }
      }
      return positions;
    },
    miniatureElements() {
      return (this.definition.elementos || [])
        .filter((item) => item.visivel !== false)
        .slice(0, 12);
    },
  },
  methods: {
    miniatureStyle(item) {
      return {
        left: `${(Number(item.x_mm || 0) / this.labelWidth) * 100}%`,
        top: `${(Number(item.y_mm || 0) / this.labelHeight) * 100}%`,
        width: `${(Number(item.largura_mm || 0) / this.labelWidth) * 100}%`,
        height: `${(Number(item.altura_mm || 0) / this.labelHeight) * 100}%`,
        backgroundColor:
          item.tipo === "rectangle"
            ? "transparent"
            : item.estilo?.cor || "#3f4254",
        borderColor: item.estilo?.cor_borda || "#3f4254",
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.sheet-preview-card {
  padding: 18px;

  &.compact {
    padding: 14px;

    .sheet-stage {
      min-height: 145px;
      margin-top: 11px;
      padding: 10px;
    }

    .sheet-warning,
    .sheet-ok {
      margin-top: 10px;
      padding: 7px 9px;
    }
  }
}

.sheet-preview-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  h6 {
    margin: 2px 0 0;
    color: #3f4254;
    font-weight: 700;
  }
}

.etq-panel-kicker {
  color: #7e8299;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.sheet-count {
  padding: 4px 7px;
  border-radius: 5px;
  background: #f0ebff;
  color: #8950fc;
  font-size: 9px;
  font-weight: 700;
}

.sheet-stage {
  min-height: 165px;
  margin: 16px -4px 12px;
  padding: 14px;
  border-radius: 9px;
  background-color: #eef1f5;
  background-image: radial-gradient(#d3d7e1 0.6px, transparent 0.6px);
  background-size: 12px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
}

.sheet-page {
  position: relative;
  flex: 0 0 auto;
  min-width: 8px;
  min-height: 8px;
  background: #606978;
  border: 1px solid #4c5564;
  box-shadow: 0 5px 15px rgba(31, 45, 61, 0.22);
  overflow: hidden;
}

.sheet-printable-area {
  position: absolute;
  background: #fff;
  box-shadow: inset 0 0 0 1px rgba(31, 45, 61, 0.08);
  pointer-events: none;
}

.sheet-label {
  position: absolute;
  box-sizing: border-box;
  border-style: solid;
  overflow: hidden;

  > span {
    position: absolute;
    display: block;
    opacity: 0.68;
  }

  .mini-barcode {
    background: repeating-linear-gradient(
      90deg,
      #181c32 0,
      #181c32 1px,
      transparent 1px,
      transparent 2px
    );
  }

  .mini-qrcode {
    background-image: repeating-conic-gradient(
      #181c32 0 25%,
      transparent 0 50%
    );
    background-size: 3px 3px;
  }

  .mini-logo {
    border: 1px dashed #7e8299;
    background: transparent !important;
  }

  .mini-line {
    height: 1px !important;
  }

  .mini-rectangle {
    border: 1px solid;
  }
}

.sheet-measures {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  color: #7e8299;
  font-size: 9px;

  strong {
    color: #3f4254;
  }

  i {
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: #b5b5c3;
  }
}

.sheet-warning,
.sheet-ok {
  margin-top: 14px;
  padding: 10px;
  border-radius: 8px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 10px;

  strong,
  span {
    display: block;
  }

  span + span {
    margin-top: 3px;
  }
}

.sheet-warning {
  background: #fff4de;
  color: #9d650d;
}

.sheet-ok {
  background: #e8fff3;
  color: #16875f;
}
</style>
